import React, { useState } from "react";
import { useAdminPanelStore } from "features/AdminPanel/store/useAdminPanelStore";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { isRequestSuccessful } from "utils/Utils";
import { requestVerification } from "../../../RequestVerificationStripe/utils/RequestVerification.utils";
import { REQUEST_VERIFICATION_KRA_CONFIG } from "../../constants/RequestVerificationKRA.constants";
import { FEATURE_STATUS } from "features/Account/Account.constants";
import { useAccountSectionUnlocking } from "features/Account/utils/useAccountSectionChange";
import { FEATURE_KEYS } from "features/Account/modules/Features/constants/FeatureKeys.constants";
import { LOGIN_FORM_VALIDATION } from "../../config/formConfig";

export const useRequestVerificationKRAModal = (config = {}) => {
  const { notify } = useNotifications();
  const sectionConfig = useAdminPanelStore((state) => state.sectionConfig);
  const subSectionConfig = sectionConfig[config.key];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
  const { handleSectionChange } = useAccountSectionUnlocking();

  const requestVerificationKey = useAdminPanelStore(
    (state) => state.requestVerificationKey
  );

  const requestVerificationSubmitFormKey = useAdminPanelStore(
    (state) => state.requestVerificationSubmitFormKey
  );

  const setRequestVerificationKey = useAdminPanelStore(
    (state) => state.setRequestVerificationKey
  );

  const setRequestVerificationSubmitFormKey = useAdminPanelStore(
    (state) => state.setRequestVerificationSubmitFormKey
  );

  const setSectionConfig = useAdminPanelStore(
    (state) => state.setSectionConfig
  );

  const requestVerificationConfig =
    REQUEST_VERIFICATION_KRA_CONFIG[subSectionConfig?.status] ||
    REQUEST_VERIFICATION_KRA_CONFIG.default ||
    {};

  const closeRequestVerificationModal = () => {
    setRequestVerificationKey(null);
  };

  const openRequestVerificationModal = () => {
    setRequestVerificationKey(config.key);
  };

  const closeRequestVerificationSubmitFormModal = () => {
    setRequestVerificationSubmitFormKey(null);
  };

  const openRequestVerificationSubmitFormModal = () => {
    setRequestVerificationSubmitFormKey(config.key);
  };

  const updateStatusAfterSubmission = () => {
    setSectionConfig({
      ...sectionConfig,
      [config.key]: {
        ...(sectionConfig[config.key] || {}),
        status: FEATURE_STATUS.ACCESS_REQUESTED,
      },
    });
  };

  // Documents configuration for upload modal
  const documents = [
    {
      key: "address_proof",
      title: "Address Proof",
      subtitle: (
        <p className=" tw-text-gray-500">
          Rental agreement / Internet / Electricity bill
        </p>
      ),
      required: true,
    },
    {
      key: "cin_certificate",
      title: "CIN Certificate",
      required: true,
    },
    {
      key: "company_pan",
      title: "Company PAN",
      required: true,
    },
    {
      key: "regulated_entity_certificate",
      title: "Regulated Entity Certificate",
      required: true,
    },
    {
      key: "authorised_signatory_documents",
      title: "Authorised Signatory Masked Aadhar card & PAN Card",
      required: true,
    },
    {
      key: "gst_declaration",
      title: "GST / NON-GST declaration",
      required: true,
    },
    {
      key: "onboarding_form",
      title: "ONBOARDING FORM",
      subtitle: (
        <span className=" tw-text-gray-500">
          You only need to fill in points 1, 2, 3, 4, 5 & 8 and ignore rest of{" "}
          <a
            href="https://docs.google.com/document/d/1Af4MIweoiSesMMUFPHrhk-B4QGfG32GQl7aiPJof9gc/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-mt-1 tw-underline"
          >
            Download Form
          </a>
        </span>
      ),
      required: true,
    },
  ];

  const handleFileUpload = (documentKey, fileUrl, fileName, form) => {
    form.change(documentKey, {
      url: fileUrl,
      name: fileName,
    });
    // Clear any validation errors for this field
    form.blur(documentKey);
    notify(
      `${
        documents.find((doc) => doc.key === documentKey)?.title
      } uploaded successfully`,
      notification_color_keys.success
    );
  };

  const handleFileClear = (documentKey, form) => {
    form.change(documentKey, null);
    notify(
      `${documents.find((doc) => doc.key === documentKey)?.title} removed`,
      notification_color_keys.info
    );
  };

  const validateForm = (values) => {
    const errors = {};

    documents
      .filter((doc) => doc.required)
      .forEach((doc) => {
        if (!values[doc.key] || !values[doc.key].url) {
          errors[doc.key] = `${doc.title} is required`;
        }
      });

    return errors;
  };

  const handleDocumentSubmit = async (values, onClose, onSubmit) => {
    try {
      setIsSubmitting(true);

      // Prepare payload for API
      const payload = {
        documents: values,
        feature_key: config.key,
      };

      // Call the API
      const response = await requestVerification(payload);

      if (isRequestSuccessful(response.status)) {
        notify(
          "Documents submitted successfully!",
          notification_color_keys.success
        );

        // Update the status to ACCESS_REQUESTED
        updateStatusAfterSubmission();

        // Close all modals
        closeRequestVerificationModal();
        onClose();

        onSubmit(values);
      }
    } catch (error) {
      notify(
        "Failed to submit documents. Please try again.",
        notification_color_keys.error
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginCredentialsSubmit = async (values, onSuccess, onError) => {
    try {
      setIsLoginSubmitting(true);

      // Prepare payload for API
      const payload = {
        feature_key: FEATURE_KEYS.KRA_CVL_CREDS,
        feature_value: {
          login_id: values.loginId,
          password: values.password,
        },
      };

      // Submit credentials using handleSectionChange
      onSuccess();
      await handleSectionChange({ payload });

      // Show success notification
      notify(
        "Login credentials submitted successfully!",
        notification_color_keys.success
      );
    } catch (error) {
      notify(
        "Failed to submit login credentials. Please try again.",
        notification_color_keys.error
      );
      onError();
    } finally {
      setIsLoginSubmitting(false);
    }
  };

  const validateLoginForm = (values) => {
    const errors = {};

    if (!values.loginId?.trim()) {
      errors.loginId = LOGIN_FORM_VALIDATION.loginId.required;
    }

    if (!values.password?.trim()) {
      errors.password = LOGIN_FORM_VALIDATION.password.required;
    }

    return errors;
  };

  return {
    requestVerificationConfig,
    openRequestVerificationModal,
    closeRequestVerificationModal,
    requestVerificationKey,
    openRequestVerificationSubmitFormModal,
    closeRequestVerificationSubmitFormModal,
    requestVerificationSubmitFormKey,
    documents,
    handleFileUpload,
    handleFileClear,
    validateForm,
    handleDocumentSubmit,
    isSubmitting,
    isLoginSubmitting,
    featureStatus: subSectionConfig?.status,
    handleLoginCredentialsSubmit,
    areCVLLoginCredSubmitted:
      sectionConfig?.[FEATURE_KEYS.KRA_CVL_CREDS]?.value || false,
    validateLoginForm,
  };
};
